/**
 * Suggested players (A4) — a frequency-ranked shortlist of players to
 * quick-add to a note/clip, derived from who the coach already tagged
 * on the SAME match's other notes and clips. There is no "lineup"
 * concept in the data model, so "who's likely involved in this match"
 * is approximated by tagging history on the match's existing feedback
 * artifacts — the players a coach keeps coming back to for this game.
 *
 * Pure function (no React) so it's unit-testable in isolation and the
 * component just memoizes a call to it.
 */

type HasPlayerIds = { player_ids: string[] };

export type SuggestedPlayersInput = {
  /** Notes already attached to the selected match (railNotes). */
  notes: ReadonlyArray<HasPlayerIds>;
  /** Clips already attached to the selected match (matchClips). */
  clips: ReadonlyArray<HasPlayerIds>;
  /** Player ids that exist in the current roster — suggestions are
   * filtered to these so a stale tag (a since-removed player) never
   * surfaces as a chip. */
  rosterPlayerIds: ReadonlyArray<string>;
  /** Players already tagged on the composer in progress — excluded so
   * we never suggest someone the coach has already added. */
  alreadyTagged: ReadonlyArray<string>;
  /** Cap on the number of suggestions (default 5). */
  limit?: number;
};

/**
 * Returns up to `limit` player ids, ranked by how often they appear
 * across the match's notes + clips (most-tagged first). Ties break by
 * first appearance so the order is deterministic. Players not in the
 * roster, or already tagged on the current composer, are excluded.
 */
export function suggestedPlayerIds({
  notes,
  clips,
  rosterPlayerIds,
  alreadyTagged,
  limit = 5,
}: SuggestedPlayersInput): string[] {
  const roster = new Set(rosterPlayerIds);
  const tagged = new Set(alreadyTagged);

  // Tally frequency, recording first-seen order for stable tie-break.
  const counts = new Map<string, number>();
  const order = new Map<string, number>();
  let seen = 0;
  for (const item of [...notes, ...clips]) {
    for (const id of item.player_ids) {
      if (!roster.has(id) || tagged.has(id)) continue;
      counts.set(id, (counts.get(id) ?? 0) + 1);
      if (!order.has(id)) order.set(id, seen++);
    }
  }

  return [...counts.keys()]
    .sort((a, b) => {
      const byCount = (counts.get(b) ?? 0) - (counts.get(a) ?? 0);
      if (byCount !== 0) return byCount;
      return (order.get(a) ?? 0) - (order.get(b) ?? 0);
    })
    .slice(0, limit);
}
